import { config } from '../config';

type FlagsMap = Record<string, string>;

let _flags: FlagsMap = {};
let _loadedAt = 0;
let _loadPromise: Promise<FlagsMap> | null = null;

const TTL_MS = 60_000;

const normalizeAppId = (appId?: string): string | undefined => {
  if (!appId) return undefined;
  const s = String(appId).trim().toUpperCase();
  return s ? s : undefined;
};

const normalizeCC = (cc?: string): string | undefined => {
  if (!cc) return undefined;
  const s = String(cc).trim().toLowerCase();
  return s && s.length === 2 ? s : undefined;
};

export const flagSrc = (cc: string) => `https://flagcdn.com/${cc}.svg`;

export const getFlagUrl = (appId?: string): string | undefined => {
  const key = normalizeAppId(appId);
  if (!key) return undefined;
  const cc = normalizeCC(_flags[key]);
  return cc ? flagSrc(cc) : undefined;
};

export const ccToEmoji = (cc?: string): string => {
  if (!cc) return '';
  const upper = cc.toUpperCase();
  if (upper.length !== 2) return '';
  const codePoints = Array.from(upper).map((ch) => 127397 + ch.codePointAt(0)!);
  return String.fromCodePoint(...codePoints);
};

export const getFlagEmoji = (appId?: string): string => {
  const key = normalizeAppId(appId);
  if (!key) return '';
  const cc = normalizeCC(_flags[key]);
  return ccToEmoji(cc);
};

export const setFlagsMap = (map: FlagsMap) => {
  _flags = map || {};
  _loadedAt = Date.now();
};

export const ensureAppFlagsLoaded = async (force: boolean = false): Promise<FlagsMap> => {
  const now = Date.now();
  if (!force && _loadedAt && (now - _loadedAt) < TTL_MS && Object.keys(_flags).length > 0) return _flags;
  if (!force && _loadPromise) return _loadPromise;
  const base = config.getApiUrl().replace(/\/$/, '');
  const token = (() => {
    try { return localStorage.getItem('access_token'); } catch { return null; }
  })();
  _loadPromise = fetch(`${base}/apps/flags`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    cache: 'no-cache',
  })
    .then(async (r) => {
      if (!r.ok) throw new Error(`flags_fetch_failed_${r.status}`);
      return (await r.json()) as FlagsMap;
    })
    .then((data) => {
      const next: FlagsMap = {};
      for (const [k, v] of Object.entries(data || {})) {
        const key = normalizeAppId(k);
        const cc = normalizeCC(v);
        if (key && cc) next[key] = cc;
      }
      setFlagsMap(next);
      return _flags;
    })
    .finally(() => {
      _loadPromise = null;
    });
  return _loadPromise;
};


